import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Fiber from "effect/Fiber";
import * as Layer from "effect/Layer";
import * as ManagedRuntime from "effect/ManagedRuntime";
import * as Scope from "effect/Scope";
import * as Semaphore from "effect/Semaphore";
import * as Stream from "effect/Stream";
import { EditorCapabilities } from "@linempo/application";
import { Cancelled, frameCount, TIMELINE_TARGET } from "@linempo/render-domain";
import { acceptJobEvent, acceptsPreviewFrame, MAX_SUBTITLE_CHARACTERS } from "@linempo/worker-protocol";
import { freezeMetadata } from "@linempo/kernel";
import { asRenderError, checkedProject, checkedSettings, idleJob, initialClientState, workerError } from "./model.js";
import { openWorkerConnection } from "./transport.js";
export const acquireWorkerClient = (options) => makeWorkerClient(options, openWorkerConnection);
export const workerClientLayer = (options) => Layer.effect(EditorCapabilities, acquireWorkerClient(options));
export function makeWorkerClient(options, openConnection) {
    return Effect.gen(function* () {
        const ownerScope = yield* Effect.scope;
        const runtime = ManagedRuntime.make(Layer.empty);
        const rootScope = Scope.makeUnsafe();
        const workspaceScope = Scope.forkUnsafe(rootScope);
        let state = initialClientState();
        const listeners = new Set();
        const transition = Semaphore.makeUnsafe(1);
        const connectionLock = Semaphore.makeUnsafe(1);
        const nativeReadLock = Semaphore.makeUnsafe(1);
        const id = options.createId ?? (() => crypto.randomUUID());
        const responseTimeoutMs = options.responseTimeoutMs ?? 2_000;
        const startupTimeoutMs = options.startupTimeoutMs ?? 30_000;
        const fileTimeoutMs = options.fileTimeoutMs ?? 30_000;
        let generation = 0;
        const requestEpoch = { prepare: 0, export: 0, generate: 0 };
        let disposed = false;
        let disposal = null;
        let previewSession = null;
        let preparation = null;
        let exporting = null;
        let generating = null;
        let generatePromise = null;
        let generateKey = null;
        let preparePromise = null;
        let exportPromise = null;
        let prepareKey = null;
        let exportKey = null;
        let importPending = false;
        const starting = { prepare: null, export: null, generate: null };
        let sequence = 0;
        let seekRunning = false;
        let nextSeek = null;
        let frameSlot = null;
        let expectedFrame = null;
        let presenter = null;
        const urls = new Map();
        const snapshot = () => state;
        const emit = (event) => { freezeMetadata(event); for (const listener of listeners) {
            try {
                listener(event);
            }
            catch { /* An observer cannot corrupt native ownership. */ }
        } };
        const update = (change) => {
            if (disposed)
                return;
            const previous = state;
            state = change(state);
            if (previous.prepare !== state.prepare)
                emit({ kind: "prepare", state: state.prepare, plan: state.prepare.plan });
            if (previous.generate !== state.generate && state.generate.animationId)
                emit({ kind: "generate", state: state.generate, animationId: state.generate.animationId, result: state.generate.result });
            if (previous.export !== state.export)
                emit({ kind: "export", state: state.export, artifact: state.export.artifact });
            if (state.preview.frame && previous.preview.frame !== state.preview.frame)
                emit({ kind: "frame", frame: state.preview.frame });
            if (state.error && previous.error !== state.error)
                emit({ kind: "failure", error: state.error });
        };
        const report = (error) => update((s) => ({ ...s, error }));
        const fail = (message) => Effect.fail(workerError("protocol", message));
        const attempt = (body) => Effect.try({ try: body, catch: asRenderError });
        const run = (effect) => {
            if (disposed)
                return Promise.reject(workerError("unavailable", "Worker client has been disposed"));
            return runtime.runPromise(Effect.gen(function* () {
                const fiber = yield* effect.pipe(Effect.catchCause((cause) => Effect.fail(asRenderError(Cause.squash(cause)))), Effect.forkIn(rootScope));
                return yield* Fiber.join(fiber);
            })).catch((error) => { const typed = asRenderError(error); if (typed._tag !== "Cancelled")
                report(typed); throw typed; });
        };
        const workspace = (effect) => Effect.acquireUseRelease(Effect.sync(() => update((s) => ({ ...s, workspace: { ...s.workspace, pending: s.workspace.pending + 1 } }))), () => effect, () => Effect.sync(() => update((s) => ({ ...s, workspace: { ...s.workspace, pending: Math.max(0, s.workspace.pending - 1) } })))).pipe(Effect.forkIn(workspaceScope), Effect.flatMap(Fiber.join));
        const clearFrame = () => {
            frameSlot?.bitmap.close();
            frameSlot = null;
            expectedFrame = null;
            nextSeek?.resolve(false);
            nextSeek = null;
            sequence++;
        };
        const deliverFrame = () => {
            if (!frameSlot || !expectedFrame)
                return;
            const frame = frameSlot;
            frameSlot = null;
            if (frame.descriptor.transferId !== expectedFrame.transferId || !acceptsPreviewFrame(expectedFrame, frame.descriptor) || frame.descriptor.requestSequence !== sequence || frame.descriptor.timeMs !== expectedFrame.timeMs || frame.descriptor.dimensions.width !== expectedFrame.dimensions.width || frame.descriptor.dimensions.height !== expectedFrame.dimensions.height) {
                frame.bitmap.close();
                return;
            }
            try {
                presenter?.(frame.bitmap, frame.descriptor);
                update((s) => ({ ...s, preview: { ...s.preview, frame: frame.descriptor, seeking: false } }));
            }
            catch (error) {
                report(workerError("protocol", `Preview presenter failed: ${String(error)}`));
            }
            finally {
                frame.bitmap.close();
                expectedFrame = null;
            }
        };
        const receiveFrame = (frame) => {
            if (disposed) {
                frame.bitmap.close();
                return;
            }
            const plan = snapshot().prepare.plan;
            const job = preparation;
            if (snapshot().preview.frame?.requestSequence === frame.descriptor.requestSequence || !job || job.cancelled || job.generation !== generation || !plan || !acceptsPreviewFrame({ ...job.context, planId: plan.planId, requestSequence: sequence }, frame.descriptor)) {
                frame.bitmap.close();
                return;
            }
            frameSlot?.bitmap.close();
            frameSlot = frame;
            deliverFrame();
        };
        const connectionOptions = (kind, onFailure) => ({
            kind, createWorker: options.createWorker, onFrame: kind === "preview" ? receiveFrame : (frame) => frame.bitmap.close(), onFailure,
            heartbeatIntervalMs: options.heartbeatIntervalMs ?? 1_000, startupTimeoutMs, responseTimeoutMs, fileTimeoutMs,
        });
        const getPreview = () => connectionLock.withPermit(Effect.gen(function* () {
            if (previewSession && !previewSession.dead && previewSession.connection)
                return previewSession.connection;
            if (previewSession)
                yield* Scope.close(previewSession.scope, Exit.void);
            const scope = yield* Scope.fork(rootScope);
            const session = { scope, connection: null, dead: false };
            previewSession = session;
            const connection = yield* Scope.provide(openConnection(connectionOptions("preview", (error) => {
                session.dead = true;
                if (previewSession === session) {
                    clearFrame();
                    const old = preparation;
                    if (old && old.connection === session.connection) {
                        // Closing the job scope must preserve a transport failure, not report user cancellation.
                        old.failure ??= error;
                        preparation = null;
                        runtime.runFork(Scope.close(old.scope, Exit.void));
                        update((s) => ({ ...s, prepare: { ...s.prepare, status: "failed", plan: null, error }, preview: { ...s.preview, frame: null, seeking: false } }));
                    }
                    const oldGeneration = generating;
                    if (oldGeneration && oldGeneration.connection === session.connection) {
                        oldGeneration.failure ??= error;
                        generating = null;
                        runtime.runFork(Scope.close(oldGeneration.scope, Exit.void));
                        update((s) => ({ ...s, generate: { ...s.generate, status: "failed", result: null, error } }));
                    }
                    report(error);
                }
            })), scope).pipe(Effect.timeoutOrElse({ duration: startupTimeoutMs, orElse: () => Effect.fail(workerError("timeout", "Preview Worker initialization timed out")) }), Effect.onError(() => Scope.close(scope, Exit.void)));
            session.connection = connection;
            return connection;
        }));
        const interruptStarting = (target) => Effect.gen(function* () {
            for (const kind of ["prepare", "export", "generate"]) {
                const pending = starting[kind];
                if (!pending || (target !== "all" && target !== kind))
                    continue;
                pending.cancelled = true;
                update((s) => ({ ...s, [kind]: { ...s[kind], status: "cancelling" } }));
                yield* Scope.close(pending.scope, Exit.void);
            }
        });
        const begin = (kind, acquire) => Effect.gen(function* () {
            const scope = yield* Scope.fork(rootScope);
            const pending = { scope, id: id(), cancelled: false };
            starting[kind] = pending;
            update((s) => ({ ...s, [kind]: { ...s[kind], status: "running", jobId: pending.id, error: null }, error: null }));
            const connection = yield* acquire(scope).pipe(Effect.forkIn(scope), Effect.flatMap(Fiber.join), Effect.catchCause((cause) => Effect.fail(pending.cancelled ? new Cancelled({ jobId: pending.id }) : asRenderError(Cause.squash(cause)))), Effect.tapError((error) => Effect.sync(() => update((s) => ({ ...s, [kind]: { ...s[kind], status: pending.cancelled ? "cancelled" : "failed", error: pending.cancelled ? null : error } })))), Effect.onError(() => Scope.close(scope, Exit.void)), Effect.ensuring(Effect.sync(() => { if (starting[kind] === pending)
                starting[kind] = null; })));
            return { scope, connection };
        });
        const invalidateQueued = (target) => {
            for (const kind of ["prepare", "export", "generate"])
                if (target === "all" || target === kind)
                    requestEpoch[kind]++;
        };
        const closeJob = (job) => Effect.gen(function* () {
            job.cancelled = true;
            update((s) => ({ ...s, [job.kind]: { ...s[job.kind], status: "cancelling" } }));
            yield* job.connection.run(job.connection.rpc.Cancel({ _tag: "Cancel", ...job.context })).pipe(Effect.timeoutOrElse({ duration: responseTimeoutMs, orElse: () => Effect.fail(workerError("timeout", "Worker cancellation cleanup timed out")) }), Effect.catch((error) => Effect.sync(() => {
                job.connection.terminate(error);
                if (job.kind !== "export" && previewSession?.connection === job.connection)
                    previewSession.dead = true;
                report(error);
            })));
            yield* Scope.close(job.scope, Exit.void);
            if (job.kind !== "export" && previewSession?.dead) {
                yield* Scope.close(previewSession.scope, Exit.void);
                previewSession = null;
            }
            update((s) => ({ ...s, [job.kind]: { ...s[job.kind], status: "cancelled" } }));
        });
        const cancelJobs = (target) => Effect.gen(function* () {
            if (target === "all" || target === "prepare") {
                clearFrame();
                const old = preparation;
                preparation = null;
                if (old)
                    yield* closeJob(old);
                update((s) => ({ ...s, prepare: { ...s.prepare, plan: null }, preview: { ...s.preview, frame: null, seeking: false } }));
            }
            if (target === "all" || target === "export") {
                const old = exporting;
                exporting = null;
                if (old)
                    yield* closeJob(old);
            }
            if (target === "all" || target === "generate") {
                const old = generating;
                generating = null;
                if (old)
                    yield* closeJob(old);
            }
        });
        const fileHandle = (file, descriptor) => {
            const url = URL.createObjectURL(file);
            let active = true;
            const release = () => { if (!active)
                return; active = false; URL.revokeObjectURL(url); urls.get(descriptor.id)?.delete(release); if (!urls.get(descriptor.id)?.size)
                urls.delete(descriptor.id); };
            if (!urls.has(descriptor.id))
                urls.set(descriptor.id, new Set());
            urls.get(descriptor.id).add(release);
            return { url, descriptor, release };
        };
        const accept = (job, event) => {
            if (disposed || job.cancelled || job.generation !== generation || (job.kind === "prepare" ? preparation : job.kind === "generate" ? generating : exporting) !== job)
                return;
            const next = acceptJobEvent(job.state, event);
            if (next === job.state)
                return;
            if ((event._tag === "Prepared" && job.kind !== "prepare") || (event._tag === "Completed" && job.kind !== "export") || (event._tag === "Generated" && (job.kind !== "generate" || event.result.animationId !== job.animationId)))
                throw workerError("protocol", "Unexpected terminal event for this job kind");
            if (event._tag === "Prepared") {
                const actual = event.target ?? TIMELINE_TARGET;
                const expected = job.target ?? TIMELINE_TARGET;
                if (actual._tag !== expected._tag || (actual._tag === "animation" && expected._tag === "animation" && actual.animationId !== expected.animationId))
                    throw workerError("protocol", "Prepared target differs from request");
            }
            job.state = next;
            update((s) => {
                const view = s[job.kind];
                if (event._tag === "Progress")
                    return { ...s, [job.kind]: { ...view, progress: event } };
                if (event._tag === "Prepared" && job.kind === "prepare")
                    return { ...s, prepare: { ...s.prepare, status: "ready", plan: event } };
                if (event._tag === "Generated" && job.kind === "generate")
                    return { ...s, generate: { ...s.generate, status: "completed", result: event.result } };
                if (event._tag === "Completed" && job.kind === "export")
                    return { ...s, export: { ...s.export, status: "completed", artifact: event.artifact } };
                if (event._tag === "Failed")
                    return { ...s, error: event.error, [job.kind]: { ...view, status: "failed", error: event.error } };
                if (event._tag === "Cancelled")
                    return { ...s, [job.kind]: { ...view, status: "cancelled" } };
                return { ...s, [job.kind]: { ...view, status: "running" } };
            });
        };
        const runJob = (job, events) => Effect.gen(function* () {
            const fiber = yield* job.connection.run(Stream.runForEach(events, (event) => Effect.sync(() => accept(job, event)))).pipe(Effect.forkIn(job.scope));
            yield* Fiber.join(fiber);
            if (job.cancelled || job.generation !== generation)
                return yield* Effect.fail(new Cancelled({ jobId: job.context.jobId }));
            const last = job.state.lastEvent;
            if (!last || job.state.status !== "terminal")
                return yield* fail("Worker stream ended without a terminal event");
            if (last._tag === "Failed")
                return yield* Effect.fail(last.error);
            if (last._tag === "Cancelled")
                return yield* Effect.fail(new Cancelled({ jobId: job.context.jobId }));
            return last;
        }).pipe(Effect.catchCause((cause) => Effect.fail(job.cancelled || job.generation !== generation ? new Cancelled({ jobId: job.context.jobId }) : job.failure ?? asRenderError(Cause.squash(cause)))), Effect.tapError((error) => Effect.sync(() => {
            if (!job.cancelled && job.generation === generation)
                update((s) => ({ ...s, error, [job.kind]: { ...s[job.kind], status: "failed", error } }));
        })));
        const newJob = (kind, project, connection, scope, animationId, target) => {
            const context = { jobId: id(), projectId: project.id, projectRevision: project.revision };
            return { kind, ...(target === undefined ? {} : { target }), ...(animationId === undefined ? {} : { animationId }), context, connection, scope, generation, state: { context, status: "pending", lastEvent: null }, cancelled: false, failure: null };
        };
        const pumpSeeks = () => {
            if (seekRunning || !nextSeek || disposed)
                return;
            seekRunning = true;
            const request = nextSeek;
            nextSeek = null;
            const task = Effect.gen(function* () {
                const job = preparation;
                const plan = snapshot().prepare.plan;
                if (!job || job.cancelled || request.generation !== generation || request.sequence !== sequence || !plan)
                    return false;
                const descriptor = yield* job.connection.run(job.connection.rpc.Seek({ _tag: "Seek", ...job.context, planId: plan.planId, requestSequence: request.sequence, timeMs: request.timeMs, target: request.target })).pipe(Effect.forkIn(job.scope), Effect.flatMap(Fiber.join));
                if (disposed || job !== preparation || job.cancelled || request.generation !== generation || request.sequence !== sequence)
                    return false;
                if (!acceptsPreviewFrame({ ...job.context, planId: plan.planId, requestSequence: request.sequence }, descriptor) || descriptor.timeMs !== request.timeMs || descriptor.dimensions.width !== request.target.width || descriptor.dimensions.height !== request.target.height)
                    return yield* fail("Seek response differs from request");
                expectedFrame = descriptor;
                deliverFrame();
                return true;
            });
            void run(task.pipe(Effect.catchCause((cause) => request.sequence !== sequence || request.generation !== generation || disposed
                ? Effect.succeed(false) : Effect.fail(asRenderError(Cause.squash(cause)))))).then(request.resolve, request.reject).finally(() => { seekRunning = false; if (request.sequence === sequence)
                update((s) => ({ ...s, preview: { ...s.preview, seeking: false } })); pumpSeeks(); });
        };
        const commands = {
            loadProject: (projectId) => run(workspace(Effect.gen(function* () {
                const connection = yield* getPreview();
                const input = yield* connection.run(connection.rpc.LoadProject({ projectId }));
                return input === null ? null : yield* attempt(() => checkedProject(input));
            }))),
            saveProject: (project, expectedRevision) => run(workspace(Effect.gen(function* () {
                const connection = yield* getPreview();
                yield* connection.run(connection.rpc.SaveProject({ project: checkedProject(project), expectedRevision }));
            }))),
            parseSubtitle: (source) => run(workspace(Effect.gen(function* () {
                if (source.length > MAX_SUBTITLE_CHARACTERS)
                    return yield* fail("字幕文件超过 2 Mi 字符限制");
                const connection = yield* getPreview();
                return yield* connection.run(connection.rpc.ParseSubtitle({ source }));
            }))),
            importAsset: (native, assetId = id()) => {
                if (importPending)
                    return Promise.reject(workerError("busy", "已有素材正在导入"));
                importPending = true;
                update((s) => ({ ...s, workspace: { ...s.workspace, importing: true } }));
                return run(workspace(Effect.gen(function* () {
                    const file = yield* attempt(() => { if (!(native instanceof File))
                        throw workerError("protocol", "Asset input must be a File"); return native; });
                    const connection = yield* getPreview();
                    const transferId = id();
                    yield* connection.sendAsset(file, transferId);
                    return yield* connection.run(connection.rpc.ImportAsset({ transferId, assetId })).pipe(Effect.timeoutOrElse({ duration: fileTimeoutMs, orElse: () => Effect.suspend(() => { const error = workerError("timeout", "Asset import timed out"); connection.terminate(error); return Effect.fail(error); }) }));
                }))).finally(() => { importPending = false; update((s) => ({ ...s, workspace: { ...s.workspace, importing: false } })); });
            },
            readAsset: (asset, thumbnail = false) => run(workspace(nativeReadLock.withPermit(Effect.gen(function* () {
                const connection = yield* getPreview();
                const transferId = id();
                const result = yield* connection.receiveFile("asset-output", transferId, connection.run(connection.rpc.ReadAsset({ asset, transferId, ...(thumbnail ? { thumbnail: true } : {}) })).pipe(Effect.map((descriptor) => ({ descriptor, value: asset }))));
                if (!thumbnail && result.file.size !== asset.byteLength)
                    return yield* fail("Asset File differs from asset descriptor");
                return yield* attempt(() => fileHandle(result.file, result.value));
            })))),
            readArtifact: (artifactId) => run(workspace(nativeReadLock.withPermit(Effect.gen(function* () {
                const connection = yield* getPreview();
                const transferId = id();
                const result = yield* connection.receiveFile("artifact-output", transferId, connection.run(connection.rpc.ReadArtifact({ artifactId, transferId })).pipe(Effect.map((response) => ({ descriptor: response.file, value: response.artifact }))));
                if (result.value.id !== artifactId || result.file.size !== result.value.byteLength)
                    return yield* fail("Artifact File differs from committed descriptor");
                return yield* attempt(() => fileHandle(result.file, result.value));
            })))),
            removeArtifact: (artifactId) => run(workspace(Effect.gen(function* () {
                const connection = yield* getPreview();
                yield* connection.run(connection.rpc.RemoveArtifact({ artifactId }));
                for (const release of urls.get(artifactId) ?? [])
                    release();
                update((s) => s.export.artifact?.id === artifactId ? { ...s, export: { ...s.export, artifact: null } } : s);
            }))),
            prepare: (input, target = TIMELINE_TARGET) => {
                const key = JSON.stringify([input.id, input.revision, target]);
                if (preparePromise)
                    return prepareKey === key ? preparePromise : Promise.reject(workerError("busy", "Cancel the active preparation before preparing another revision"));
                prepareKey = key;
                const epoch = requestEpoch.prepare;
                const requestId = id();
                const promise = run(Effect.gen(function* () {
                    const job = yield* transition.withPermit(Effect.gen(function* () {
                        if (disposed || epoch !== requestEpoch.prepare)
                            return yield* Effect.fail(new Cancelled({ jobId: requestId }));
                        const project = yield* attempt(() => checkedProject(input));
                        if (target._tag === "timeline")
                            yield* attempt(() => frameCount(project.durationMs));
                        if (generating || starting.generate)
                            return yield* Effect.fail(workerError("busy", "Animation generation is already running"));
                        yield* cancelJobs("prepare");
                        const { connection, scope } = yield* begin("prepare", () => getPreview());
                        const job = newJob("prepare", project, connection, scope, undefined, target);
                        preparation = job;
                        update((s) => ({ ...s, prepare: { ...idleJob(), status: "running", jobId: job.context.jobId, plan: null }, error: null }));
                        return { job, project };
                    }));
                    const event = yield* runJob(job.job, job.job.connection.rpc.Prepare({ _tag: "Prepare", ...job.job.context, project: job.project, target }));
                    if (event._tag !== "Prepared")
                        return yield* fail("Prepare did not return a plan");
                    return event;
                })).finally(() => { if (preparePromise === promise)
                    preparePromise = null; });
                preparePromise = promise;
                return promise;
            },
            inspectAnimations: (input, animationIds) => run(workspace(Effect.gen(function* () {
                const project = yield* attempt(() => checkedProject(input));
                const connection = yield* getPreview();
                return yield* connection.run(connection.rpc.InspectAnimations({ project, animationIds }));
            }))),
            generate: (input, animationId) => {
                const key = JSON.stringify([input.id, input.revision, animationId]);
                if (generatePromise)
                    return generateKey === key ? generatePromise : Promise.reject(workerError("busy", "Another animation generation is running"));
                generateKey = key;
                const epoch = requestEpoch.generate;
                const requestId = id();
                const promise = run(Effect.gen(function* () {
                    const setup = yield* transition.withPermit(Effect.gen(function* () {
                        if (disposed || epoch !== requestEpoch.generate)
                            return yield* Effect.fail(new Cancelled({ jobId: requestId }));
                        const project = yield* attempt(() => checkedProject(input));
                        if (starting.prepare || preparation?.state.status === "pending" || preparation?.state.status === "running")
                            return yield* Effect.fail(workerError("busy", "Preview preparation is already running"));
                        yield* cancelJobs("prepare");
                        update((s) => ({ ...s, generate: { ...idleJob(), animationId, result: null } }));
                        const { connection, scope } = yield* begin("generate", () => getPreview());
                        const job = newJob("generate", project, connection, scope, animationId);
                        generating = job;
                        update((s) => ({ ...s, generate: { ...idleJob(), status: "running", jobId: job.context.jobId, animationId, result: null }, error: null }));
                        return { job, project };
                    }));
                    return yield* Effect.gen(function* () {
                        const event = yield* runJob(setup.job, setup.job.connection.rpc.Generate({ _tag: "Generate", ...setup.job.context, project: setup.project, animationId }));
                        if (event._tag !== "Generated")
                            return yield* fail("Generate did not return animation geometry metadata");
                        return event.result;
                    }).pipe(Effect.ensuring(Effect.gen(function* () { if (generating === setup.job)
                        generating = null; yield* Scope.close(setup.job.scope, Exit.void); })));
                })).finally(() => { if (generatePromise === promise)
                    generatePromise = null; });
                generatePromise = promise;
                return promise;
            },
            seek: (requestedPlan, timeMs, target = { width: 960, height: 540 }) => {
                if (disposed)
                    return Promise.reject(workerError("unavailable", "Worker client has been disposed"));
                const plan = snapshot().prepare.plan;
                if (!plan || plan.planId !== requestedPlan.planId || plan.jobId !== requestedPlan.jobId || plan.projectId !== requestedPlan.projectId || plan.projectRevision !== requestedPlan.projectRevision || !Number.isFinite(timeMs) || timeMs < 0 || timeMs > plan.durationMs || !Number.isInteger(target.width) || !Number.isInteger(target.height) || Math.min(target.width, target.height) <= 0 || Math.max(target.width, target.height) > 1280)
                    return Promise.reject(workerError("protocol", "Seek requires a prepared plan, valid time and a preview no larger than 1280px"));
                nextSeek?.resolve(false);
                frameSlot?.bitmap.close();
                frameSlot = null;
                expectedFrame = null;
                sequence++;
                update((s) => ({ ...s, preview: { ...s.preview, requestedTimeMs: timeMs, seeking: true } }));
                const promise = new Promise((resolve, reject) => { nextSeek = { sequence, timeMs, target, generation, resolve, reject }; });
                pumpSeeks();
                return promise;
            },
            export: (input, plan, inputSettings) => {
                const key = JSON.stringify([input.id, input.revision, plan.planId, inputSettings]);
                if (exportPromise)
                    return exportKey === key ? exportPromise : Promise.reject(workerError("busy", "An export with different inputs is already running"));
                exportKey = key;
                const epoch = requestEpoch.export;
                const requestId = id();
                const promise = run(Effect.gen(function* () {
                    const setup = yield* transition.withPermit(Effect.gen(function* () {
                        if (disposed || epoch !== requestEpoch.export)
                            return yield* Effect.fail(new Cancelled({ jobId: requestId }));
                        const project = yield* attempt(() => checkedProject(input));
                        const settings = yield* attempt(() => checkedSettings(inputSettings));
                        if (!plan || plan.target?._tag === "animation" || plan.projectRevision !== project.revision || plan.projectId !== project.id)
                            return yield* fail("请先准备当前修订的预览计划");
                        yield* attempt(() => frameCount(project.durationMs));
                        const { connection, scope } = yield* begin("export", (scope) => Scope.provide(openConnection(connectionOptions("export", report)), scope).pipe(Effect.timeoutOrElse({ duration: startupTimeoutMs, orElse: () => Effect.fail(workerError("timeout", "Export Worker initialization timed out")) })));
                        const job = newJob("export", project, connection, scope);
                        exporting = job;
                        update((s) => ({ ...s, export: { ...idleJob(), status: "running", jobId: job.context.jobId, artifact: null }, error: null }));
                        return { job, plan, settings };
                    }));
                    return yield* Effect.gen(function* () {
                        const event = yield* runJob(setup.job, setup.job.connection.rpc.Export({ _tag: "Export", ...setup.job.context, planId: setup.plan.planId, settings: setup.settings }));
                        if (event._tag !== "Completed")
                            return yield* fail("Export did not return a completed artifact");
                        return event.artifact;
                    }).pipe(Effect.ensuring(Effect.gen(function* () { if (exporting === setup.job)
                        exporting = null; yield* Scope.close(setup.job.scope, Exit.void); })));
                })).finally(() => { if (exportPromise === promise)
                    exportPromise = null; });
                exportPromise = promise;
                return promise;
            },
            cancel: (target = "all") => {
                invalidateQueued(target);
                return run(Effect.andThen(interruptStarting(target), transition.withPermit(cancelJobs(target))));
            },
        };
        const disposalEffect = Effect.promise(async () => {
            if (disposal)
                return disposal;
            disposed = true;
            generation++;
            invalidateQueued("all");
            clearFrame();
            presenter = null;
            disposal = runtime.runPromise(Effect.gen(function* () {
                yield* interruptStarting("all");
                // Interrupt workspace startup before awaiting job cleanup; cancellation still has its barrier.
                yield* Scope.close(workspaceScope, Exit.void);
                yield* transition.withPermit(cancelJobs("all"));
            })).finally(async () => {
                try {
                    await runtime.runPromise(Scope.close(rootScope, Exit.void));
                }
                finally {
                    for (const releases of urls.values())
                        for (const release of releases)
                            release();
                    urls.clear();
                    listeners.clear();
                    await runtime.dispose();
                }
            });
            return disposal;
        });
        yield* Scope.addFinalizer(ownerScope, disposalEffect);
        const invoke = (body) => Effect.tryPromise({ try: body, catch: asRenderError });
        // A canceled caller cannot receive a late Promise result. Retain ownership until the
        // resumed Effect executes the handoff, releasing late/abandoned URL leases immediately.
        const invokeLease = (body) => Effect.callback((resume) => {
            let waiting = true;
            let pending = null;
            try {
                void body().then((lease) => {
                    if (!waiting) {
                        lease.release();
                        return;
                    }
                    pending = lease;
                    resume(Effect.sync(() => { waiting = false; pending = null; return lease; }));
                }, (error) => { if (waiting) {
                    waiting = false;
                    resume(Effect.fail(asRenderError(error)));
                } });
            }
            catch (error) {
                waiting = false;
                resume(Effect.fail(asRenderError(error)));
            }
            return Effect.sync(() => { waiting = false; pending?.release(); pending = null; });
        });
        const service = {
            loadProject: (projectId) => invoke(() => commands.loadProject(projectId)),
            saveProject: (project, expectedRevision) => invoke(() => commands.saveProject(project, expectedRevision)),
            parseSubtitle: (source) => invoke(() => commands.parseSubtitle(source)),
            importAsset: (native, assetId) => invoke(() => commands.importAsset(native, assetId)),
            readAsset: (asset) => invokeLease(() => commands.readAsset(asset)),
            readThumbnail: (asset) => invokeLease(() => commands.readAsset(asset, true)),
            readArtifact: (artifactId) => invokeLease(() => commands.readArtifact(artifactId)),
            removeArtifact: (artifactId) => invoke(() => commands.removeArtifact(artifactId)),
            inspectAnimations: (project, animationIds) => invoke(() => commands.inspectAnimations(project, animationIds)),
            generate: (project, animationId) => invoke(() => commands.generate(project, animationId)).pipe(Effect.onInterrupt(() => invoke(() => commands.cancel("generate")).pipe(Effect.ignore))),
            prepare: (project, target) => invoke(() => commands.prepare(project, target)).pipe(Effect.onInterrupt(() => invoke(() => commands.cancel("prepare")).pipe(Effect.ignore))),
            seek: (plan, timeMs, target) => invoke(() => commands.seek(plan, timeMs, target)),
            export: (project, plan, settings) => invoke(() => commands.export(project, plan, settings)).pipe(Effect.onInterrupt(() => invoke(() => commands.cancel("export")).pipe(Effect.ignore))),
            cancel: (target) => invoke(() => commands.cancel(target)),
            subscribe: (listener) => { if (disposed)
                return () => { }; listeners.add(listener); return () => { listeners.delete(listener); }; },
            presentFrames: (callback) => { if (disposed)
                return () => { }; presenter = callback; return () => { if (presenter === callback)
                presenter = null; }; },
        };
        return service;
    });
}
